import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const GOALS = ["Get cloud certified", "Land a cloud/DevOps job", "Upskill at work", "Interview preparation", "Just exploring"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    country: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    experience_level: "Beginner",
    learning_goal: GOALS[0],
  });
  const [busy, setBusy] = useState(false);

  const finish = async (save) => {
    setBusy(true);
    try {
      if (save) await api.post("/me/profile", f);
      navigate("/billing", { replace: true });
    } catch (e) { toast.error("Could not save your profile."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#06080c] text-white p-6" data-testid="onboarding-page">
      <div className="w-full max-w-lg rounded-2xl bg-[#0f131b] border border-white/10 p-8 shadow-2xl">
        <div className="flex items-center gap-2 text-[#38bdf8] mb-1"><Rocket className="h-5 w-5" /> <span className="text-xs font-semibold uppercase tracking-wider">Welcome aboard</span></div>
        <h1 className="text-2xl font-bold mb-1">Tell us a bit about you</h1>
        <p className="text-white/50 text-sm mb-6">This helps us tailor your labs. It takes 20 seconds — you can skip it.</p>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60">Country</label>
              <input data-testid="ob-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} placeholder="e.g. United States"
                className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#38bdf8]/60" />
            </div>
            <div>
              <label className="text-xs text-white/60">Time zone</label>
              <input data-testid="ob-timezone" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })}
                className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#38bdf8]/60" />
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60">Experience level</label>
            <div className="mt-1 flex gap-2" data-testid="ob-level">
              {LEVELS.map((l) => (
                <button key={l} onClick={() => setF({ ...f, experience_level: l })}
                  className={`flex-1 h-10 rounded-lg border text-sm ${f.experience_level === l ? "border-[#38bdf8] bg-[#38bdf8]/15" : "border-white/10 hover:border-white/25"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-white/60">Primary goal</label>
            <select data-testid="ob-goal" value={f.learning_goal} onChange={(e) => setF({ ...f, learning_goal: e.target.value })}
              className="mt-1 w-full h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm outline-none focus:border-[#38bdf8]/60">
              {GOALS.map((g) => <option key={g} value={g} className="bg-[#0f131b]">{g}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-7 flex items-center gap-3">
          <button data-testid="ob-continue" disabled={busy} onClick={() => finish(true)}
            className="flex-1 h-11 rounded-lg bg-[#38bdf8] text-[#06121b] font-semibold flex items-center justify-center gap-2 hover:bg-[#5cc9fb] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to labs <ArrowRight className="h-4 w-4" /></>}
          </button>
          <button data-testid="ob-skip" disabled={busy} onClick={() => finish(false)} className="h-11 px-4 rounded-lg text-white/60 hover:text-white text-sm">Skip</button>
        </div>
      </div>
    </div>
  );
}
